import { ExternalLink, File as FileIcon, FileText, Image as ImageIcon } from 'lucide-react'

import { cn } from '../cn'

export interface CitationChipAttachment {
  contentType: string
  fileSize: number
  fileName: string
}

export interface CitationChipCitation {
  url: string
  label?: string
  /** When set, the chip renders as an attachment pill (file-type icon + name + size) instead of a link pill. */
  attachment?: CitationChipAttachment
}

export interface CitationChipProps {
  citation: CitationChipCitation
  className?: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/**
 * Single citation pill — link or attachment variant.
 *
 *  - **Link**:        external URL — small pill, ExternalLink icon, label.
 *  - **Attachment**:  when `citation.attachment` is set — file-type icon
 *                     + truncated name + formatted size.
 *
 * Lifted from `apps/agent/client/src/pages/chat.tsx::CitationChip` to be
 * the canonical primitive every Ordo agent surface uses.
 */
function CitationChip({ citation, className }: CitationChipProps) {
  if (citation.attachment) {
    const att = citation.attachment
    const Icon = att.contentType.startsWith('image/')
      ? ImageIcon
      : att.contentType === 'application/pdf'
        ? FileText
        : FileIcon
    return (
      <a
        href={citation.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'typo-meta inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-foreground transition-colors hover:bg-muted',
          className,
        )}>
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="max-w-[200px] truncate font-medium">{att.fileName}</span>
        <span className="typo-caption shrink-0">{formatBytes(att.fileSize)}</span>
      </a>
    )
  }
  return (
    <a
      href={citation.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'typo-meta inline-flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 transition-colors hover:bg-muted hover:text-foreground',
        className,
      )}>
      <ExternalLink className="h-3 w-3 shrink-0" />
      <span className="max-w-[240px] truncate">{citation.label ?? safeHostname(citation.url)}</span>
    </a>
  )
}

export { CitationChip }
